import InfoTooltip from 'components/InfoTooltip';
import LoadingSpinner from 'components/LoadingSpinner';
import React, { useEffect, useRef, useState } from 'react';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { Product } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import {
  Button,
  ButtonType,
  Col,
  Row,
  Table,
  Tooltip
} from 'syngenta-digital-cropwise-react-ui-kit';
import EditIcon from 'assets/edit_outlined.svg?react';
import ShowIcon from 'assets/show.svg?react';
import HideIcon from 'assets/hide.svg?react';
import FormattedNumber from 'components/FormattedNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import LayoutConstants from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutConstants';
import useProductsTable from './useProductsTable';
import TableActions from '../../../TableActions';
import EditCarrierDrawer from '../EditCarrierDrawer';

import './styles.css';
import { RECENTLY_UPDATED_CLASSNAMES } from './constants';
import { useProductStep } from '../../hooks/useProductStep';

const PAGE_SIZE = 20;
const isCarrierRow = (record: Product) => record.isCarrier;

/** Inline-block wrapper that prevents its content from being split across lines */
const NoBreak: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = ''
}) => (
  <span
    className={className}
    style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
  >
    {children}
  </span>
);

const ProductNameCell = ({
  name,
  manufacturer
}: {
  name: string;
  manufacturer: string;
}) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const manufacturerRef = useRef<HTMLDivElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);
  const [isManufacturerTruncated, setIsManufacturerTruncated] = useState(false);

  useEffect(() => {
    if (nameRef.current) {
      setIsNameTruncated(
        nameRef.current.scrollWidth > nameRef.current.clientWidth
      );
    }
    if (manufacturerRef.current) {
      setIsManufacturerTruncated(
        manufacturerRef.current.scrollWidth >
          manufacturerRef.current.clientWidth
      );
    }
  }, [name, manufacturer]);

  return (
    <>
      <Tooltip title={isNameTruncated ? name : ''} color="#232630">
        <div ref={nameRef} className="text-neutral-90 font-medium truncate">
          {name}
        </div>
      </Tooltip>
      <Tooltip
        title={isManufacturerTruncated ? manufacturer : ''}
        color="#232630"
      >
        <div ref={manufacturerRef} className="text-neutral-60 text-xs truncate">
          {manufacturer}
        </div>
      </Tooltip>
    </>
  );
};

interface ProductsTableProps {
  onProductEdit?: (product: Product, index: number) => void;
}

const ProductsTable = ({ onProductEdit }: ProductsTableProps) => {
  const {
    products,
    isTankInfoEnabled,
    isCarrierRowHidden,
    currentPage,
    setCurrentPage,
    toggleCarrierRowVisibility,
    totalCost,
    isEditCarrierDrawerOpen,
    setIsEditCarrierDrawerOpen,
    deleteProduct,
    isLoadingCarrier
  } = useProductsTable();

  const { isUpdatingTankInfo, recentlyUpdated } = useProductStep();

  const setRecentlyUpdatedClassNames = (record: Product, columnKey: string) => {
    if (!isUpdatingTankInfo || (record.isCarrier && isCarrierRowHidden))
      return '';

    const productApplicationId = record.isCarrier
      ? record.id
      : record?.productApplicationId;

    if (!productApplicationId) return '';

    const recentlyUpdatedFields =
      recentlyUpdated?.products?.[productApplicationId] ?? [];

    if (recentlyUpdatedFields.includes(columnKey))
      return RECENTLY_UPDATED_CLASSNAMES;

    return '';
  };

  let columns: any[] = [
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      height: 60,
      width: 180,
      align: 'left' as const,
      render: (_: any, record: Product) => {
        if (isCarrierRow(record)) {
          if (isCarrierRowHidden) {
            return (
              <Row prefixCls="syt-antd-row">
                Carrier
                <InfoTooltip
                  className="ml-1"
                  placement="topRight"
                  title={LayoutStrings.products.tankInfo.hiddenCarrierTooltip}
                />
              </Row>
            );
          }
          return (
            <>
              <div className="text-neutral-60 truncate">Carrier</div>
              <Row className="mt-6px mb-1 h-6" gutter={8}>
                <Col>
                  <Button
                    type={ButtonType.outline}
                    icon={
                      <EditIcon className="ml-2px" width={18} height={18} />
                    }
                    size="small"
                    onClick={() => setIsEditCarrierDrawerOpen(true)}
                    aria-label={`Edit ${record.name}`}
                  />
                </Col>
                <Col className="py-2px" flex="auto">
                  <div className="text-neutral-90 font-medium truncate">
                    {record.name}
                  </div>
                </Col>
              </Row>
            </>
          );
        }

        return (
          <ProductNameCell
            name={record.name}
            manufacturer={record.manufacturer ?? ''}
          />
        );
      },
      onCell: (record: Product) => ({
        colSpan: isCarrierRow(record) && isCarrierRowHidden ? 6 : 1
      })
    },
    {
      title: 'Rate per Acre',
      dataIndex: 'ratePerAcre',
      key: 'ratePerAcre',
      height: 60,
      width: 144,
      align: 'right' as const,
      render: (_: any, record: Product) =>
        record.ratePerAcre === '' || typeof record.ratePerAcre !== 'number' ? (
          '---'
        ) : (
          <div className="text-neutral-90">
            <div className="flex flex-wrap justify-end items-baseline">
              <span
                className={`p-2 rounded ${setRecentlyUpdatedClassNames(
                  record,
                  'ratePerAcre'
                )}`}
                style={{ transform: 'translateX(8px)' }}
              >
                <FormattedNumber roundedTo={2}>
                  {record.ratePerAcre}
                </FormattedNumber>
                <NoBreak className="ml-1">
                  {`${getAbbreviatedUnit(record.ratePerAreaUnit)}/ac`}
                </NoBreak>
              </span>
            </div>
          </div>
        ),
      onCell: (record: Product) => ({
        colSpan: isCarrierRow(record) && isCarrierRowHidden ? 0 : 1
      })
    }
  ];

  if (isTankInfoEnabled) {
    columns.push({
      title: 'Rate per Tank',
      dataIndex: 'ratePerTank',
      key: 'ratePerTank',
      height: 60,
      width: 153,
      align: 'right' as const,
      render: (_: any, record: Product) =>
        record.totalProduct === '' ? (
          '---'
        ) : (
          <div className="text-neutral-90">
            <div className="flex flex-wrap justify-end items-baseline">
              <span
                className={`p-2 rounded ${setRecentlyUpdatedClassNames(
                  record,
                  'ratePerTank'
                )}`}
                style={{ transform: 'translateX(8px)' }}
              >
                <FormattedNumber roundedTo={2}>
                  {record.ratePerTank}
                </FormattedNumber>
                <NoBreak className="ml-1">
                  {`${getAbbreviatedUnit(record.ratePerTankUnit)}/tank`}
                </NoBreak>
              </span>
            </div>
          </div>
        ),
      onCell: (record: Product) => ({
        colSpan: isCarrierRow(record) && isCarrierRowHidden ? 0 : 1
      })
    });
  }
  columns = [
    ...columns,
    {
      title: 'Total Product',
      dataIndex: 'totalProduct',
      key: 'totalProduct',
      height: 60,
      width: 140,
      align: 'right' as const,
      render: (_: any, record: Product) =>
        record.totalProduct === '' ? (
          '---'
        ) : (
          <div className="text-neutral-90">
            <div className="flex flex-wrap justify-end items-baseline">
              <span
                className={`p-2 rounded ${setRecentlyUpdatedClassNames(
                  record,
                  'totalProduct'
                )}`}
                style={{ transform: 'translateX(8px)' }}
              >
                <FormattedNumber roundedTo={2}>
                  {record.totalProduct}
                </FormattedNumber>
                <NoBreak className="ml-1">
                  {getAbbreviatedUnit(record.totalProductUnit)}
                </NoBreak>
              </span>
            </div>
          </div>
        ),
      onCell: (record: Product) => ({
        colSpan: isCarrierRow(record) && isCarrierRowHidden ? 0 : 1
      })
    },
    {
      title: 'Appl. Acres',
      dataIndex: 'appliedAreaValue',
      key: 'appliedAreaValue',
      height: 60,
      width: 135,
      align: 'right' as const,
      render: (_: any, record: Product) => (
        <div className="text-neutral-90 text-right">
          <FormattedNumber roundedTo={2}>
            {record.appliedAreaValue}
          </FormattedNumber>
          <span className="ml-1">ac</span>
          <p className="text-neutral-60 ml-1 text-xs">
            {`${record?.coveragePercent?.toFixed(2)}%`}
          </p>
        </div>
      ),
      onCell: (record: Product) => ({
        colSpan: isCarrierRow(record) && isCarrierRowHidden ? 0 : 1
      })
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      height: 60,
      width: 111,
      align: 'right' as const,
      render: (_: any, record: Product) => {
        const priceValue = record.priceValue ?? record.averagePricePerUnit ?? 0;
        const priceUnit =
          record.priceUnit ?? record.averagePricePerUnitUnit ?? record.stdunit;

        return (
          <div className="text-neutral-90 font-medium text-right">
            {!record.totalCost || Number.isNaN(record.totalCost) ? (
              '---'
            ) : (
              <div>
                <FormattedNumber unit="usCurrency">
                  {record.totalCost}
                </FormattedNumber>
                <p className="text-neutral-60 ml-1 text-xs">
                  <FormattedNumber unit="usCurrency">
                    {priceValue}
                  </FormattedNumber>
                  <NoBreak>{`/${getAbbreviatedUnit(priceUnit)}`}</NoBreak>
                </p>
              </div>
            )}
          </div>
        );
      },
      onCell: (record: Product) => ({
        colSpan: isCarrierRow(record) && isCarrierRowHidden ? 0 : 1
      })
    },
    {
      title: '',
      key: 'actions',
      width: 105,
      align: 'right' as const,
      render: (_: any, record: Product, idx: number) =>
        isCarrierRow(record) ? (
          <div className="flex justify-start" style={{ marginLeft: -16 }}>
            <Button
              type={ButtonType.outline}
              className={`shadow-none border-none text-inherit ${
                isCarrierRowHidden ? 'h-5' : ''
              } px-2 hover:cursor-pointer hover:bg-blue-10 hover:text-blue-50 focus:bg-blue-10 focus:text-blue-50 group`}
              icon={
                <span
                  style={{ transition: 'all 0.3s' }}
                  className="text-neutral-60 group-hover:text-blue-50 group-focus:text-blue-50"
                >
                  {isCarrierRowHidden ? <ShowIcon /> : <HideIcon />}
                </span>
              }
              onClick={() => toggleCarrierRowVisibility(record)}
            >
              {isCarrierRowHidden ? 'Show' : 'Hide'}
            </Button>
          </div>
        ) : (
          <TableActions
            onEdit={() => onProductEdit?.(record, idx)}
            onDelete={() => deleteProduct(idx)}
            editButtonLabel={`Edit ${record.name}`}
            deleteButtonLabel={`Delete ${record.name}`}
          />
        )
    }
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderFooter = (total: number, range: [number, number]) => (
    <div className="flex bg-white items-center justify-end">
      <div className="flex items-center text-neutral-90 text-base px-3">
        <span className="font-medium">Total Cost:</span>
        <span className="font-semibold ml-1">
          <FormattedNumber unit="usCurrency">{totalCost}</FormattedNumber>
        </span>
      </div>
      <span className="text-neutral-60 ml-4">
        {`${range[0]} - ${range[1]} of ${total}`}
      </span>
    </div>
  );

  return (
    <div
      className={`application-products-table${
        isTankInfoEnabled ? ' tank-info-enabled' : ''
      }`}
      data-testid="products-table"
    >
      {isLoadingCarrier && <LoadingSpinner message="" className="pt-8" />}
      <div style={isLoadingCarrier ? { display: 'none' } : undefined}>
        <Table
          columns={columns}
          dataSource={products}
          rowKey={record => record.productApplicationId || record.id}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: products.length - (isCarrierRowHidden ? 1 : 0),
            onChange: handlePageChange,
            showSizeChanger: false,
            showPrevNextJumpers: false,
            position: ['bottomRight'],
            showTotal: renderFooter
          }}
          scroll={{
            y: isTankInfoEnabled
              ? LayoutConstants.TABLE_VERTICAL_PRODUCTS_SCROLL_WITH_TANK_INFO
              : LayoutConstants.TABLE_VERTICAL_PRODUCTS_SCROLL
          }}
        />
      </div>
      <EditCarrierDrawer
        open={isEditCarrierDrawerOpen}
        onClose={() => setIsEditCarrierDrawerOpen(false)}
      />
    </div>
  );
};

export default ProductsTable;
